import { setStepUpToken } from '@platform/api';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { StepUpContext } from './StepUpContext';
import { StepUpDialog } from './StepUpDialog';

// The token is intentionally NOT auto-cleared on client-side expiry — that would
// unmount any gated form mid-edit and destroy in-progress input. Consumers that gate
// actions re-evaluate `expiresAt > new Date()` at call time and re-prompt before
// sending (see `useStepUpAction`), so the common idle-user case is covered.

const STORAGE_KEY = 'stepUpToken';

function loadFromSession(): { token: string; expiresAt: Date } | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const { token, expiresAt } = JSON.parse(raw) as { token: string; expiresAt: string };
    const expiry = new Date(expiresAt);
    if (expiry <= new Date()) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { token, expiresAt: expiry };
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [initial] = useState(loadFromSession);
  const [token, setTokenState] = useState<string | null>(initial?.token ?? null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(initial?.expiresAt ?? null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const pendingRef = useRef<{ resolve: () => void; reject: () => void } | null>(null);

  // Sync restored token to module on mount; subsequent updates flow through setToken/clearToken,
  // which set the module-level token synchronously so customFetch sees it immediately.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only sync; `initial` is stable from useState
  useEffect(() => {
    if (initial?.token) {
      setStepUpToken(initial.token);
    }
  }, []);

  const clearToken = () => {
    setTokenState(null);
    setExpiresAt(null);
    setStepUpToken(null);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const getIsValid = () => token != null && expiresAt != null && expiresAt > new Date();

  const setToken = (newToken: string, newExpiresAt: string) => {
    setTokenState(newToken);
    setExpiresAt(new Date(newExpiresAt));
    setStepUpToken(newToken);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ token: newToken, expiresAt: newExpiresAt }));

    // Resolve the pending promise and close dialog
    setDialogOpen(false);
    pendingRef.current?.resolve();
    pendingRef.current = null;
  };

  useEffect(() => {
    return () => {
      pendingRef.current?.reject();
    };
  }, []);

  const requestStepUp = (): Promise<void> => {
    pendingRef.current?.reject();
    return new Promise<void>((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      setDialogOpen(true);
    });
  };

  function handleDialogCancel() {
    setDialogOpen(false);
    pendingRef.current?.reject();
    pendingRef.current = null;
  }

  const value = { token, expiresAt, getIsValid, requestStepUp, setToken, clearToken };

  return (
    <StepUpContext.Provider value={value}>
      {children}
      <StepUpDialog open={dialogOpen} onCancel={handleDialogCancel} onSuccess={setToken} />
    </StepUpContext.Provider>
  );
}
